import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, map, of, switchMap, withLatestFrom } from 'rxjs';
import * as UsersActions from './user.actions';
import { selectUsers, selectUsersQuery } from './user.selectors';
import { UserService } from '../services/user.service';
import { UserDto } from '@shared/dto';
import { listResultActions$ } from '@shared/utils/pagination.utils';

@Injectable()
export class UserEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly userService = inject(UserService);

  readUsers$ = createEffect(() => this.actions$.pipe(
    ofType(UsersActions.readUsers),
    exhaustMap(({ query }) =>
      listResultActions$(
        query ? this.userService.read(undefined, { pagination: query }) : this.userService.read(),
        query,
        ({ data, total }) => UsersActions.readUsersSuccess({ data, total }),
        (error) => UsersActions.readUsersFailure({ error }),
      )),
  ));

  // Detail: resolve from the already-loaded list first (no HTTP), otherwise fetch
  // the single entity. The store is the source of truth that both the detail page
  // and the breadcrumb read from.
  readUser$ = createEffect(() => this.actions$.pipe(
    ofType(UsersActions.readUser),
    withLatestFrom(this.store.select(selectUsers)),
    switchMap(([{ userId }, list]) => {
      const cached = list?.find((user) => user.id === userId);
      if (cached) {
        return of(UsersActions.readUserSuccess({ user: cached }));
      }

      return this.userService.readOne({ userId }).pipe(
        map((user) => UsersActions.readUserSuccess({ user })),
        catchError((error) => of(UsersActions.readUserFailure({ error }))),
      );
    }),
  ));

  createUser$ = createEffect(() => this.actions$.pipe(
    ofType(UsersActions.createUser),
    exhaustMap(({ dto }) => this.userService.create(dto).pipe(
      map((user: UserDto) => UsersActions.createUserSuccess({ user })),
      catchError((error) => of(UsersActions.createUserFailure({ error })))
    ))
  ));

  updateUser$ = createEffect(() => this.actions$.pipe(
    ofType(UsersActions.updateUser),
    exhaustMap(({ userId, dto }) => this.userService.update({ userId }, dto).pipe(
      map((user: Partial<UserDto>) => UsersActions.updateUserSuccess({ user })),
      catchError((error) => of(UsersActions.updateUserFailure({ error })))
    ))
  ));

  deleteUser$ = createEffect(() => this.actions$.pipe(
    ofType(UsersActions.deleteUser),
    exhaustMap(({ userId }) => this.userService.delete({ userId }).pipe(
      map((user: UserDto) => UsersActions.deleteUserSuccess({ user })),
      catchError((error) => of(UsersActions.deleteUserFailure({ error })))
    ))
  ));

  restoreUser$ = createEffect(() => this.actions$.pipe(
    ofType(UsersActions.restoreUser),
    exhaustMap(({ userId }) => this.userService.restore({ userId }).pipe(
      map((user: UserDto) => UsersActions.restoreUserSuccess({ user })),
      catchError((error) => of(UsersActions.restoreUserFailure({ error })))
    ))
  ));

  // After a mutation, refetch the current page so counts/pagination stay correct.
  reloadAfterMutation$ = createEffect(() => this.actions$.pipe(
    ofType(
      UsersActions.createUserSuccess,
      UsersActions.updateUserSuccess,
      UsersActions.deleteUserSuccess,
      UsersActions.restoreUserSuccess,
    ),
    withLatestFrom(this.store.select(selectUsersQuery)),
    map(([, query]) => UsersActions.readUsers(query ?? undefined)),
  ));
}
